import type {
  ApplySanctionPayload,
  BrandSanctionLevel,
  SanctionPreview,
} from '../api/payment/brand-sanction.api';
import { validateDecisionReason } from './brand-dispute';
import { caForVnd, formatCa } from './individual-order';
import { isLowerLevel, SANCTION_LEVELS } from './brand-sanction';

// Form xử phạt phía admin (spec §4.2, §4.3, ruling RB10). Kiểm trước khi gửi chỉ để báo sớm, BE quyết cuối.

export const COMPENSATION_LIMITS = { minVnd: 10_000, maxVnd: 100_000_000, reasonMax: 500 } as const;
export const LOWER_REASON_LIMITS = { minLength: 10, maxLength: 500 } as const;
export const ADMIN_NOTE_LIMITS = { minLength: 10, maxLength: 500 } as const;
export const DEFAULT_COMPENSATION_REASON = 'Bồi thường do người đặt đơn vi phạm';

export interface CompensationLine {
  creatorId: string;
  amountVnd: number;
  reason: string;
}

export interface SanctionFormState {
  level: BrandSanctionLevel;
  reason: string;
  lowerLevelReason: string;
  compensations: CompensationLine[];
}

type PreviewMoney = Pick<SanctionPreview, 'ordererKind' | 'availableVnd' | 'availableCa' | 'vndPerCa'>;

export const validateLowerLevelReason = (text: string | null | undefined): string | null => {
  const t = text?.trim() ?? '';
  return t.length < LOWER_REASON_LIMITS.minLength || t.length > LOWER_REASON_LIMITS.maxLength
    ? 'Lý do chọn mức nhẹ hơn gợi ý từ 10 đến 500 ký tự.'
    : null;
};

/** Lý do gỡ phạt / gỡ danh sách đen (RB11). */
export const validateAdminNote = (text: string | null | undefined): string | null => {
  const t = text?.trim() ?? '';
  return t.length < ADMIN_NOTE_LIMITS.minLength || t.length > ADMIN_NOTE_LIMITS.maxLength ? 'Lý do từ 10 đến 500 ký tự.' : null;
};

export interface CompensationNeed {
  unit: 'VND' | 'Ca';
  need: number;
  available: number | null;
  short: boolean;
  label: string;
  shortMessage: string | null;
}

/** Tổng cần trừ so với số dư: doanh nghiệp = tổng VND trên ví; cá nhân = tổng ceil(VND ÷ tỉ giá) từng khoản trên Ca nạp. */
export const compensationNeed = (preview: PreviewMoney, lines: CompensationLine[]): CompensationNeed => {
  if (preview.ordererKind === 'Individual') {
    const need = lines.reduce((sum, l) => sum + caForVnd(l.amountVnd, preview.vndPerCa), 0);
    const available = preview.availableCa;
    const short = available !== null && need > available;
    return {
      unit: 'Ca',
      need,
      available,
      short,
      label: `Cần trừ ${formatCa(need)} Ca nạp, đang có ${available === null ? '…' : formatCa(available)} Ca nạp`,
      shortMessage: short ? `Số dư hiện có chỉ còn ${formatCa(available as number)} Ca nạp, chưa đủ để bồi thường.` : null,
    };
  }
  const need = lines.reduce((sum, l) => sum + l.amountVnd, 0);
  const available = preview.availableVnd;
  const short = available !== null && need > available;
  return {
    unit: 'VND',
    need,
    available,
    short,
    label: `Cần trừ ${formatCa(need)}đ trong ví, số dư hiện có ${available === null ? '…' : `${formatCa(available)}đ`}`,
    shortMessage: short ? `Số dư hiện có chỉ còn ${formatCa(available as number)}đ, chưa đủ để bồi thường.` : null,
  };
};

/** Lỗi đầu tiên của form, null = gửi được. */
export const validateSanctionForm = (form: SanctionFormState, preview: SanctionPreview): string | null => {
  if (!SANCTION_LEVELS.includes(form.level)) return 'Chọn mức xử phạt.';
  const reasonError = validateDecisionReason(form.reason);
  if (reasonError) return reasonError;
  if (isLowerLevel(form.level, preview.suggestedLevel)) {
    const lowerError = validateLowerLevelReason(form.lowerLevelReason);
    if (lowerError) return lowerError;
  }
  const allowed = new Set(preview.creators.map((c) => c.creatorId));
  const lineError = form.compensations
    .map((line, i): string | null => {
      if (!allowed.has(line.creatorId)) return 'Chỉ bồi thường cho creator từng có đơn với người này.';
      if (form.compensations.findIndex((l) => l.creatorId === line.creatorId) !== i)
        return 'Mỗi creator chỉ nhận một khoản bồi thường trong một lần phạt.';
      if (
        !Number.isInteger(line.amountVnd) ||
        line.amountVnd < COMPENSATION_LIMITS.minVnd ||
        line.amountVnd > COMPENSATION_LIMITS.maxVnd
      )
        return 'Số tiền bồi thường mỗi creator từ 10.000đ đến 100.000.000đ.';
      const r = line.reason.trim();
      return !r || r.length > COMPENSATION_LIMITS.reasonMax ? 'Lý do bồi thường từ 1 đến 500 ký tự.' : null;
    })
    .find((e) => e !== null);
  if (lineError) return lineError;
  return compensationNeed(preview, form.compensations).shortMessage;
};

export const buildApplyPayload = (
  adAccountId: string,
  disputeId: string | null | undefined,
  form: SanctionFormState,
  preview: Pick<SanctionPreview, 'suggestedLevel'>
): ApplySanctionPayload => ({
  adAccountId,
  level: form.level,
  reason: form.reason.trim(),
  ...(isLowerLevel(form.level, preview.suggestedLevel) ? { lowerLevelReason: form.lowerLevelReason.trim() } : {}),
  ...(disputeId ? { disputeId } : {}),
  ...(form.compensations.length > 0
    ? {
        compensations: form.compensations.map((l) => ({
          creatorId: l.creatorId,
          amountVnd: l.amountVnd,
          reason: l.reason.trim(),
        })),
      }
    : {}),
});
